import JSZip from 'jszip';
import { Notebook, MindMapNode, NodeLink } from '../types';

/**
 * Helper: Chuyển đổi Data URI, Blob URL hoặc URL từ xa (http/https) sang Blob nhị phân
 */
async function fetchToBlob(url: string): Promise<Blob> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch ${url}`);
  return await response.blob();
}

/**
 * Service xử lý việc xuất và nhập dữ liệu dưới dạng file ZIP
 */
export const backupService = {
  /**
   * Xuất toàn bộ notebooks ra file ZIP (True Offline Backup)
   * Duyệt qua dữ liệu, tải toàn bộ ảnh (Local & Cloud) ra folder assets
   */
  async exportToZip(notebooks: Notebook[], onProgress?: (percent: number) => void): Promise<void> {
    console.log("Dịch vụ Backup: Đang chuẩn bị xuất dữ liệu offline...", notebooks.length, "notebooks");
    
    try {
      const zip = new JSZip();
      const assetsFolder = zip.folder("assets");
      
      // Tạo bản sao sâu để không làm hỏng dữ liệu gốc đang hiển thị
      const processedNotebooks = JSON.parse(JSON.stringify(notebooks));
      let assetCount = 0;

      // 1. Tính toán tổng số lượng tài nguyên cần xử lý để báo cáo tiến trình
      let totalAssets = 0;
      notebooks.forEach(nb => {
        nb.nodes.forEach(node => {
          if (node.imageBase64 && (node.imageBase64.startsWith('data:') || node.imageBase64.startsWith('blob:') || node.imageBase64.startsWith('http'))) totalAssets++;
          if (node.drawingBase64 && (node.drawingBase64.startsWith('data:') || node.drawingBase64.startsWith('blob:') || node.drawingBase64.startsWith('http'))) totalAssets++;
          if (node.attachments) {
            node.attachments.forEach(att => {
              if (att.content && (att.content.startsWith('data:') || att.content.startsWith('blob:') || att.content.startsWith('http'))) totalAssets++;
            });
          }
        });
      });

      let processedAssets = 0;
      const updateProgress = () => {
        processedAssets++;
        if (onProgress && totalAssets > 0) {
          onProgress(Math.round((processedAssets / totalAssets) * 100));
        }
      };

      // 2. Duyệt qua từng Notebook -> Node -> Attachment
      for (const nb of processedNotebooks) {
        for (const node of nb.nodes) {
          
          // Xử lý ảnh chính (Main Image)
          if (node.imageBase64 && (
              node.imageBase64.startsWith('data:') || 
              node.imageBase64.startsWith('blob:') || 
              node.imageBase64.startsWith('http')
          )) {
            try {
              const blob = await fetchToBlob(node.imageBase64);
              const mime = blob.type || node.imageMimeType || 'image/png';
              const ext = mime.split('/')[1]?.split('+')[0] || 'png';
              const fileName = `img_${node.id}.${ext}`;
              
              assetsFolder?.file(fileName, blob);
              node.imageBase64 = `assets/${fileName}`;
              node.imageMimeType = mime;
              assetCount++;
              updateProgress();
            } catch (e) {
              console.warn("Dịch vụ Backup: Không thể tải ảnh chính cho node", node.id, e);
              updateProgress();
            }
          }

          // Xử lý bản vẽ tay (Sketch/Drawing)
          if (node.drawingBase64 && (
              node.drawingBase64.startsWith('data:') || 
              node.drawingBase64.startsWith('blob:') || 
              node.drawingBase64.startsWith('http')
          )) {
            try {
              const blob = await fetchToBlob(node.drawingBase64);
              const fileName = `sketch_${node.id}.png`;
              
              assetsFolder?.file(fileName, blob);
              node.drawingBase64 = `assets/${fileName}`;
              assetCount++;
              updateProgress();
            } catch (e) {
              console.warn("Dịch vụ Backup: Không thể tải bản vẽ cho node", node.id, e);
              updateProgress();
            }
          }

          // Xử lý danh sách đính kèm (Attachments)
          if (node.attachments && Array.isArray(node.attachments)) {
            for (const att of node.attachments) {
              if (att.content && (
                  att.content.startsWith('data:') || 
                  att.content.startsWith('blob:') || 
                  att.content.startsWith('http')
              )) {
                try {
                  const blob = await fetchToBlob(att.content);
                  const mime = blob.type || att.mimeType || 'image/png';
                  const ext = mime.split('/')[1]?.split('+')[0] || 'png';
                  const fileName = `att_${att.id}.${ext}`;
                  
                  assetsFolder?.file(fileName, blob);
                  att.content = `assets/${fileName}`;
                  att.mimeType = mime;
                  assetCount++;
                  updateProgress();
                } catch (e) {
                  console.warn("Dịch vụ Backup: Không thể tải đính kèm", att.id, e);
                  updateProgress();
                }
              }
            }
          }
        }
      }

      // 3. Thêm file dữ liệu JSON
      const jsonData = JSON.stringify({
        version: "2.0",
        exportedAt: new Date().toISOString(),
        isOfflineReady: true,
        notebooks: processedNotebooks
      }, null, 2);
      
      zip.file("data.json", jsonData);
      zip.file("README.txt", `Study Board True Offline Backup\nExported: ${new Date().toLocaleString()}\nTotal Notebooks: ${notebooks.length}\nTotal Physical Assets: ${assetCount}\n\nThis backup contains all your images embedded. You can restore it even if you are offline or your cloud data is deleted.`);

      // 4. Nén và tải về
      console.log(`Dịch vụ Backup: Đã đóng gói ${assetCount} tệp tin vật lý. Đang nén ZIP...`);
      const content = await zip.generateAsync({ 
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 }
      });

      const url = URL.createObjectURL(content);
      const link = document.createElement("a");
      link.href = url;
      const dateStr = new Date().toISOString().slice(0, 10);
      link.download = `StudyBoard_FullBackup_${dateStr}.zip`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      console.log("Dịch vụ Backup: Xuất file hoàn tất.");
      if (onProgress) onProgress(100);

    } catch (error) {
      console.error("Dịch vụ Backup: Lỗi khi xuất file ZIP", error);
      throw error;
    }
  },

  /**
   * Xuất 1 notebook duy nhất ra file ZIP
   */
  async exportSingleZip(notebook: Notebook, onProgress?: (percent: number) => void, signal?: AbortSignal): Promise<void> {
    console.log(`Dịch vụ Backup: Đang xuất "${notebook.title}"...`);
    
    try {
      const zip = new JSZip();
      const assetsFolder = zip.folder("assets");
      const processedNotebook = JSON.parse(JSON.stringify(notebook));
      let assetCount = 0;

      let totalAssets = 0;
      processedNotebook.nodes.forEach((node: MindMapNode) => {
        if (node.imageBase64 && (node.imageBase64.startsWith('data:') || node.imageBase64.startsWith('blob:') || node.imageBase64.startsWith('http'))) totalAssets++;
        if (node.drawingBase64 && (node.drawingBase64.startsWith('data:') || node.drawingBase64.startsWith('blob:') || node.drawingBase64.startsWith('http'))) totalAssets++;
        if (node.attachments) {
          node.attachments.forEach(att => {
            if (att.content && (att.content.startsWith('data:') || att.content.startsWith('blob:') || att.content.startsWith('http'))) totalAssets++;
          });
        }
      });

      let processedAssets = 0;
      const updateProgress = () => {
        processedAssets++;
        if (onProgress && totalAssets > 0) {
          onProgress(Math.round((processedAssets / totalAssets) * 100));
        }
      };

      for (const node of processedNotebook.nodes) {
        if (signal?.aborted) throw new DOMException('Export cancelled', 'AbortError');
        if (node.imageBase64 && (node.imageBase64.startsWith('data:') || node.imageBase64.startsWith('blob:') || node.imageBase64.startsWith('http'))) {
          try {
            const blob = await fetchToBlob(node.imageBase64);
            const mime = blob.type || node.imageMimeType || 'image/png';
            const ext = mime.split('/')[1]?.split('+')[0] || 'png';
            const fileName = `img_${node.id}.${ext}`;
            assetsFolder?.file(fileName, blob);
            node.imageBase64 = `assets/${fileName}`;
            node.imageMimeType = mime;
            assetCount++;
            updateProgress();
          } catch (e) {
            console.warn("Dịch vụ Backup: Không thể tải ảnh chính cho node", node.id, e);
            updateProgress();
          }
        }

        if (node.drawingBase64 && (node.drawingBase64.startsWith('data:') || node.drawingBase64.startsWith('blob:') || node.drawingBase64.startsWith('http'))) {
          try {
            const blob = await fetchToBlob(node.drawingBase64);
            const fileName = `sketch_${node.id}.png`;
            assetsFolder?.file(fileName, blob);
            node.drawingBase64 = `assets/${fileName}`;
            assetCount++;
            updateProgress();
          } catch (e) {
            console.warn("Dịch vụ Backup: Không thể tải bản vẽ cho node", node.id, e);
            updateProgress();
          }
        }

        if (node.attachments && Array.isArray(node.attachments)) {
          for (const att of node.attachments) {
            if (att.content && (att.content.startsWith('data:') || att.content.startsWith('blob:') || att.content.startsWith('http'))) {
              try {
                const blob = await fetchToBlob(att.content);
                const mime = blob.type || att.mimeType || 'image/png';
                const ext = mime.split('/')[1]?.split('+')[0] || 'png';
                const fileName = `att_${att.id}.${ext}`;
                assetsFolder?.file(fileName, blob);
                att.content = `assets/${fileName}`;
                att.mimeType = mime;
                assetCount++;
                updateProgress();
              } catch (e) {
                console.warn("Dịch vụ Backup: Không thể tải đính kèm", att.id, e);
                updateProgress();
              }
            }
          }
        }
      }

      // WRAPPED IN AN ARRAY FOR COMPATIBILITY
      const jsonData = JSON.stringify({
        version: "2.0",
        exportedAt: new Date().toISOString(),
        isOfflineReady: true,
        notebooks: [processedNotebook] 
      }, null, 2);
      
      zip.file("data.json", jsonData);
      zip.file("README.txt", `Study Board True Offline Backup\nExported: ${new Date().toLocaleString()}\nNotebook: ${notebook.title}\nTotal Physical Assets: ${assetCount}`);

      const content = await zip.generateAsync({ 
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 }
      });

      const url = URL.createObjectURL(content);
      const link = document.createElement("a");
      link.href = url;
      
      // Dynamic safe filename
      const safeTitle = notebook.title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const dateStr = new Date().toISOString().slice(0, 10);
      link.download = `StudyBoard_${safeTitle}_${dateStr}.zip`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      if (onProgress) onProgress(100);

    } catch (error: any) {
      if (error?.name !== 'AbortError') {
        console.error("Dịch vụ Backup: Lỗi khi xuất file ZIP đơn", error);
      }
      throw error;
    }
  },

  /**
   * Tạo ZIP blob cho một notebook (không trigger download) — dùng cho Google Drive backup
   */
  async generateZipBlob(notebook: Notebook, onProgress?: (percent: number) => void, signal?: AbortSignal): Promise<{ blob: Blob; filename: string }> {
    const zip = new JSZip();
    const assetsFolder = zip.folder("assets");
    const processedNotebook = JSON.parse(JSON.stringify(notebook));
    let assetCount = 0;

    let totalAssets = 0;
    processedNotebook.nodes.forEach((node: MindMapNode) => {
      if (node.imageBase64 && (node.imageBase64.startsWith('data:') || node.imageBase64.startsWith('blob:') || node.imageBase64.startsWith('http'))) totalAssets++;
      if (node.drawingBase64 && (node.drawingBase64.startsWith('data:') || node.drawingBase64.startsWith('blob:') || node.drawingBase64.startsWith('http'))) totalAssets++;
      if (node.attachments) node.attachments.forEach(att => { if (att.content && (att.content.startsWith('data:') || att.content.startsWith('blob:') || att.content.startsWith('http'))) totalAssets++; });
    });

    let processedAssets = 0;
    const updateProgress = () => { processedAssets++; if (onProgress && totalAssets > 0) onProgress(Math.round((processedAssets / totalAssets) * 90)); };

    for (const node of processedNotebook.nodes) {
      if (signal?.aborted) throw new DOMException('Export cancelled', 'AbortError');
      if (node.imageBase64 && (node.imageBase64.startsWith('data:') || node.imageBase64.startsWith('blob:') || node.imageBase64.startsWith('http'))) {
        try {
          const blob = await fetchToBlob(node.imageBase64);
          const mime = blob.type || node.imageMimeType || 'image/png';
          const ext = mime.split('/')[1]?.split('+')[0] || 'png';
          const fileName = `img_${node.id}.${ext}`;
          assetsFolder?.file(fileName, blob); node.imageBase64 = `assets/${fileName}`; node.imageMimeType = mime; assetCount++;
        } catch (e) { console.warn("generateZipBlob: skip image", node.id); }
        updateProgress();
      }
      if (node.drawingBase64 && (node.drawingBase64.startsWith('data:') || node.drawingBase64.startsWith('blob:') || node.drawingBase64.startsWith('http'))) {
        try {
          const blob = await fetchToBlob(node.drawingBase64);
          assetsFolder?.file(`sketch_${node.id}.png`, blob); node.drawingBase64 = `assets/sketch_${node.id}.png`; assetCount++;
        } catch (e) { console.warn("generateZipBlob: skip sketch", node.id); }
        updateProgress();
      }
      if (node.attachments) {
        for (const att of node.attachments) {
          if (att.content && (att.content.startsWith('data:') || att.content.startsWith('blob:') || att.content.startsWith('http'))) {
            try {
              const blob = await fetchToBlob(att.content);
              const mime = blob.type || att.mimeType || 'image/png';
              const ext = mime.split('/')[1]?.split('+')[0] || 'png';
              const fileName = `att_${att.id}.${ext}`;
              assetsFolder?.file(fileName, blob); att.content = `assets/${fileName}`; att.mimeType = mime; assetCount++;
            } catch (e) { console.warn("generateZipBlob: skip att", att.id); }
            updateProgress();
          }
        }
      }
    }

    zip.file("data.json", JSON.stringify({ version: "2.0", exportedAt: new Date().toISOString(), isOfflineReady: true, notebooks: [processedNotebook] }, null, 2));
    zip.file("README.txt", `Study Board True Offline Backup\nExported: ${new Date().toLocaleString()}\nNotebook: ${notebook.title}\nTotal Physical Assets: ${assetCount}`);

    const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
    if (onProgress) onProgress(100);

    const safeTitle = notebook.title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const dateStr = new Date().toISOString().slice(0, 10);
    return { blob, filename: `StudyBoard_${safeTitle}_${dateStr}.zip` };
  },

  /**
   * Đọc file ZIP và chuyển đổi ngược lại thành mảng Notebook (Khôi phục Base64)
   */
  async importFromZip(file: File, onProgress?: (percent: number) => void): Promise<Notebook[]> {
    console.log("Dịch vụ Backup: Đang nhập dữ liệu từ file", file.name);
    
    try {
      const zip = await JSZip.loadAsync(file);
      
      const jsonFile = zip.file("data.json");
      if (!jsonFile) {
        throw new Error("Không tìm thấy file data.json. File ZIP không hợp lệ.");
      }
      
      const jsonString = await jsonFile.async("string");
      const backupData = JSON.parse(jsonString);
      const notebooks: Notebook[] = backupData.notebooks || [];
      
      console.log(`Dịch vụ Backup: Tìm thấy ${notebooks.length} sổ tay. Đang phục hồi tài nguyên vật lý...`);

      let totalItems = 0;
      notebooks.forEach(nb => nb.nodes.forEach(node => {
          if (node.imageBase64?.startsWith('assets/')) totalItems++;
          if (node.drawingBase64?.startsWith('assets/')) totalItems++;
          if (node.attachments) totalItems += node.attachments.filter(a => a.content?.startsWith('assets/')).length;
      }));

      let processedCount = 0;
      const CHUNK_SIZE = 5; 

      for (const nb of notebooks) {
        for (const node of nb.nodes) {
          
          const processAsset = async (path: string | null | undefined): Promise<string | null> => {
              if (!path || !path.startsWith('assets/')) return path || null;
              const b64 = await zip.file(path)?.async("base64");
              processedCount++;
              if (onProgress && totalItems > 0) {
                  onProgress(Math.round((processedCount / totalItems) * 100));
              }
              if (processedCount % CHUNK_SIZE === 0) {
                  await new Promise(r => setTimeout(r, 0));
              }
              if (!b64) return null;
              return b64;
          };

          const mainB64 = await processAsset(node.imageBase64);
          if (mainB64 && node.imageBase64?.startsWith('assets/')) {
              node.imageBase64 = `data:${node.imageMimeType || 'image/png'};base64,${mainB64}`;
          }

          const sketchB64 = await processAsset(node.drawingBase64);
          if (sketchB64 && node.drawingBase64?.startsWith('assets/')) {
              node.drawingBase64 = `data:image/png;base64,${sketchB64}`;
          }

          if (node.attachments && Array.isArray(node.attachments)) {
            for (const att of node.attachments) {
              const attB64 = await processAsset(att.content);
              if (attB64 && att.content?.startsWith('assets/')) {
                att.content = `data:${att.mimeType || 'image/png'};base64,${attB64}`;
              }
            }
          }
        }
      }

      if (onProgress) onProgress(100);
      console.log("Dịch vụ Backup: Phục hồi dữ liệu hoàn tất.");
      return notebooks;

    } catch (error) {
      console.error("Dịch vụ Backup: Lỗi khi nhập file ZIP", error);
      throw error;
    }
  },

  /**
   * Nhập dữ liệu từ file SimpleMind (.smmx)
   */
  async importFromSmmx(file: File, onProgress?: (percent: number) => void): Promise<Notebook> {
    console.log("Dịch vụ Backup: Đang nhập file SimpleMind (.smmx)", file.name);
    
    try {
      const zip = await JSZip.loadAsync(file);
      
      const xmlFile = zip.file("document/mindmap.xml");
      if (!xmlFile) throw new Error("File .smmx không hợp lệ: Không tìm thấy mindmap.xml");
      // Strip UTF-8 BOM (U+FEFF) — browser DOMParser in text/xml mode fails silently on BOM
      const xmlString = (await xmlFile.async("string")).replace(/^﻿/, '');
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, "text/xml");
      
      const imageMap = new Map<string, string>();
      const allFiles = Object.values(zip.files) as any[];
      const imgFiles = allFiles.filter(f => f.name.toLowerCase().startsWith("images/") && !f.dir);
      
      const totalImgs = imgFiles.length;
      const CHUNK_SIZE = 3;

      for (let i = 0; i < totalImgs; i++) {
          const imgFile = imgFiles[i];
          const fullName = imgFile.name.split('/').pop() || "";
          if (!fullName) continue;

          const b64 = await imgFile.async("base64");
          const ext = fullName.split('.').pop()?.toLowerCase() || 'png';
          const mime = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
          const dataUri = `data:${mime};base64,${b64}`;

          const nameLower = fullName.toLowerCase();
          const lastDotIndex = nameLower.lastIndexOf('.');
          const nameNoExt = lastDotIndex !== -1 ? nameLower.substring(0, lastDotIndex) : nameLower;

          imageMap.set(nameLower, dataUri);
          imageMap.set(nameNoExt, dataUri);

          if (onProgress) onProgress(Math.round(((i + 1) / totalImgs) * 80)); 
          if (i % CHUNK_SIZE === 0) await new Promise(r => setTimeout(r, 0));
      }

      const topicElements = Array.from(xmlDoc.getElementsByTagName('topic'));
      const idMap = new Map<string, string>(); 
      
      topicElements.forEach(el => {
          const smId = el.getAttribute('id');
          if (smId) idMap.set(smId, Math.random().toString(36).substring(2, 9));
      });

      const nodes: MindMapNode[] = [];
      const links: NodeLink[] = [];

      // SimpleMind's textfmt="rtf1" is NOT standard RTF. It is a lightweight
      // plain-text format stored in the XML note element, with only two escape
      // sequences:
      //   \*...\*  — bold/italic span  → convert to **...** (markdown bold)
      //   \/       — literal slash      → keep as /
      // Everything else — including { } brackets used in math notation — is
      // plain Unicode text and must be preserved as-is.
      //
      // Match \*...\* as a PAIR and MOVE any internal leading/trailing spaces
      // OUTSIDE the ** markers. CommonMark (markdown-it / TipTap) rejects bold
      // when the closing ** is preceded by a space, so "\*1 \*là" must become
      // "**1** là" (space moved outside), not "**1 **là" (space inside = no bold).
      const parseSMMXRtf1 = (text: string): string => {
          if (!text) return "";
          return text
              .replace(/\\\//g, '/')
              .replace(/\\\*([\s\S]*?)\\\*/g, (_, content) => {
                  const trimmed = content.trim();
                  if (!trimmed) return '';
                  const leading = content.slice(0, content.length - content.trimStart().length);
                  const trailing = content.slice(content.trimEnd().length);
                  return `${leading}**${trimmed}**${trailing}`;
              });
      };

      topicElements.forEach((el, idx) => {
          const smId = el.getAttribute('id') || "";
          const smParentId = el.getAttribute('parent') || "-1";
          const studyId = idMap.get(smId) || "";
          
          const rawTitle = (el.getAttribute('text') || "").replace(/\\N/g, '\n');
          const x = parseFloat(el.getAttribute('x') || "0");
          const y = parseFloat(el.getAttribute('y') || "0");
          
          // Extract ALL text content from ALL <text> elements within <children>
          const childrenEl = el.getElementsByTagName('children')[0];
          const allTextContent: string[] = [];
          
          if (childrenEl) {
              const textElements = Array.from(childrenEl.getElementsByTagName('text'));
              
              textElements.forEach(textEl => {
                  const noteEl = textEl.getElementsByTagName('note')[0];
                  if (noteEl) {
                      const rawContent = noteEl.textContent || "";
                      const textfmt = noteEl.getAttribute('textfmt') || 'plain';
                      
                      const parsed = (textfmt === 'rtf1')
                          ? parseSMMXRtf1(rawContent)
                          : rawContent;
                      // Preserve blank lines from SimpleMind: \n\n → NBSP blank paragraph
                      // so they survive as blank lines in Study Board's TipTap editor.
                      // parseMarkdown normalizes \n\n \n\n → \n\n for NodeCard view.
                      const cleaned = parsed.replace(/\n\n/g, '\n\n \n\n');

                      if (cleaned.trim()) {
                          allTextContent.push(cleaned.trim());
                      }
                  }
              });
          }
          
          const labelContent = allTextContent.join('\n\n');

          // If node text is too long (>100 chars) or contains newlines, it was
          // typed as descriptive content — move it to content, leave title empty.
          const isTitleTooLong = rawTitle.length > 100 || rawTitle.includes('\n');
          const title = isTitleTooLong ? '' : rawTitle;
          const content = isTitleTooLong
              ? rawTitle + (labelContent ? '\n\n' + labelContent : '')
              : labelContent;

          let mainImage: string | null = null;
          let mimeType: string | null = null;
          const attachments: any[] = [];

          const findImage = (name: string | null): string | null => {
              if (!name) return null;
              return imageMap.get(name.toLowerCase()) || null;
          };

          const embeddedImg = el.getElementsByTagName('embedded-image')[0];
          const embeddedName = embeddedImg?.getAttribute('name');
          const foundEmbedded = findImage(embeddedName);
          if (foundEmbedded) {
              mainImage = foundEmbedded;
              mimeType = 'image/png';
          }

          const extraImgTags = Array.from(el.getElementsByTagName('image'));
          extraImgTags.forEach(imgTag => {
              const imgName = imgTag.getAttribute('name');
              const data = findImage(imgName);
              if (data) {
                  if (!mainImage) {
                      mainImage = data;
                      mimeType = 'image/png';
                  } else if (mainImage !== data) {
                      const isDuplicate = attachments.some(a => a.content === data);
                      if (!isDuplicate) {
                          attachments.push({
                              id: Math.random().toString(36).substring(7),
                              type: 'image',
                              content: data,
                              mimeType: 'image/png',
                              createdAt: Date.now()
                          });
                      }
                  }
              }
          });

          nodes.push({
              id: studyId,
              parentId: smParentId === "-1" ? undefined : idMap.get(smParentId),
              position: { x, y },
              title,
              content,
              imageBase64: mainImage,
              imageMimeType: mimeType,
              isAnalyzing: false,
              analysis: null,
              isExpanded: true,
              attachments: attachments.length > 0 ? attachments : undefined
          });

          if (smParentId !== "-1" && idMap.has(smParentId)) {
              links.push({
                  id: Math.random().toString(36).substring(2, 9),
                  fromId: idMap.get(smParentId)!,
                  toId: studyId,
                  createdAt: Date.now()
              });
          }
          
          if (onProgress) onProgress(80 + Math.round(((idx + 1) / topicElements.length) * 20));
      });

      // Parse cross-links from <relations> section
      const relationsEl = xmlDoc.getElementsByTagName('relations')[0];
      if (relationsEl) {
          const relationTags = Array.from(relationsEl.getElementsByTagName('relation'));
          
          relationTags.forEach(rel => {
              const isHierarchical = rel.getAttribute('asHierarchy') === 'true';
              
              // Skip hierarchical relations (already handled via parent-child)
              if (isHierarchical) return;
              
              // This is a cross-link
              const smSourceId = rel.getAttribute('source');
              const smTargetId = rel.getAttribute('target');
              
              if (!smSourceId || !smTargetId) return;
              
              // Map SimpleMind IDs to Study Board IDs
              const studySourceId = idMap.get(smSourceId);
              const studyTargetId = idMap.get(smTargetId);
              
              // Only create link if both nodes exist
              if (studySourceId && studyTargetId) {
                  links.push({
                      id: Math.random().toString(36).substring(2, 9),
                      fromId: studySourceId,
                      toId: studyTargetId,
                      createdAt: Date.now()
                  });
              }
          });
      }


      console.log("Dịch vụ Backup: Nhập file SimpleMind hoàn tất.", nodes.length, "ghi chú.");
      if (onProgress) onProgress(100);

      return {
          id: Math.random().toString(36).substring(2, 9),
          title: file.name.replace(/\.[^/.]+$/, ""),
          nodes,
          links,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          nodeWidth: 'standard',
          _createdLocally: true
      };

    } catch (error) {
      console.error("Dịch vụ Backup: Lỗi khi nhập file .smmx", error);
      throw error;
    }
  },

  /**
   * Xuất 1 notebook ra file SimpleMind (.smmx)
   */
  async exportToSmmx(notebook: Notebook): Promise<void> {
    console.log(`Dịch vụ Backup: Đang xuất "${notebook.title}" sang SimpleMind...`);

    const escapeXml = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    // Word-wrap each paragraph to ~60 chars so SimpleMind note boxes stay compact
    const wrapContent = (text: string, maxWidth = 60): string =>
      text.split('\n').map(para => {
        if (para.length <= maxWidth) return para;
        const words = para.split(' ');
        const lines: string[] = [];
        let line = '';
        for (const w of words) {
          if (!line) { line = w; continue; }
          if ((line + ' ' + w).length <= maxWidth) { line += ' ' + w; }
          else { lines.push(line); line = w; }
        }
        if (line) lines.push(line);
        return lines.join('\n');
      }).join('\n');

    try {
      const zip = new JSZip();
      const imagesFolder = zip.folder("images");
      const docFolder = zip.folder("document");

      // Map StudyBoard node ID → SimpleMind integer ID
      const idMap = new Map<string, number>();
      notebook.nodes.forEach((node, i) => idMap.set(node.id, i));

      // Normalize coordinates: scale StudyBoard canvas coords to SimpleMind's ~400-2400 range
      const xs = notebook.nodes.map(n => n.position?.x ?? 0);
      const ys = notebook.nodes.map(n => n.position?.y ?? 0);
      const minX = xs.length ? Math.min(...xs) : 0;
      const maxX = xs.length ? Math.max(...xs) : 1000;
      const minY = ys.length ? Math.min(...ys) : 0;
      const maxY = ys.length ? Math.max(...ys) : 1000;
      const rangeX = maxX - minX || 1000;
      const rangeY = maxY - minY || 1000;
      const coordScale = Math.min(2000 / rangeX, 2000 / rangeY);
      const normX = (x: number) => ((x - minX) * coordScale + 400).toFixed(2);
      const normY = (y: number) => ((y - minY) * coordScale + 400).toFixed(2);

      // Parent-child pairs already encoded via parentId — skip these from <relations>
      const hierarchicalPairs = new Set(
        notebook.nodes.filter(n => n.parentId).map(n => `${n.parentId}→${n.id}`)
      );

      const topicParts: string[] = [];

      for (const node of notebook.nodes) {
        const smId = idMap.get(node.id)!;
        const smParentId = node.parentId !== undefined ? (idMap.get(node.parentId) ?? -1) : -1;
        const title = escapeXml(node.title || '');
        const x = normX(node.position?.x ?? 0);
        const y = normY(node.position?.y ?? 0);

        // Root topics get the horizontal layout declaration
        const isRoot = smParentId === -1;
        const layoutAttr = isRoot ? ' layout="strict-horizontal"' : '';
        const layoutEl = isRoot
          ? '\n        <layout mode="strict-horizontal" direction="auto" flow="default"></layout>'
          : '';

        let imageXml = '';
        if (node.imageBase64) {
          try {
            const blob = await fetchToBlob(node.imageBase64);
            const mime = blob.type || node.imageMimeType || 'image/png';
            const ext = mime.split('/')[1]?.split('+')[0] || 'png';
            const imgStem = `img_${smId}`;
            imagesFolder?.file(`${imgStem}.${ext}`, blob);
            // SimpleMind expects name WITHOUT extension, and a scale attribute
            imageXml = `\n        <embedded-image name="${imgStem}" scale="1.0"/>`;
          } catch (e) {
            console.warn('exportToSmmx: bỏ qua ảnh node', node.id, e);
          }
        }

        let childrenXml = '';
        if (node.content?.trim()) {
          const wrapped = escapeXml(wrapContent(node.content.trim()));
          // Place note to the RIGHT of the topic card.
          // SimpleMind renders images at approx pixel_width / 1.84 units.
          // Widest images are ~1283px → ~697 SM units. Use 780 to clear + 80-unit gap.
          // Text-only topics: standard topic card ~280 units wide → 320 gives 40-unit gap.
          const noteOffsetX = imageXml ? 420 : 300;
          const noteOffsetY = -20;
          childrenXml =
            `\n        <children>\n          <text x="${noteOffsetX}" y="${noteOffsetY}">\n            <note textfmt="plain">${wrapped}</note>\n          </text>\n        </children>`;
        }

        if (layoutEl || imageXml || childrenXml) {
          topicParts.push(
            `      <topic id="${smId}" parent="${smParentId}" text="${title}" textfmt="plain" x="${x}" y="${y}"${layoutAttr}>${layoutEl}${imageXml}${childrenXml}\n      </topic>`
          );
        } else {
          topicParts.push(
            `      <topic id="${smId}" parent="${smParentId}" text="${title}" textfmt="plain" x="${x}" y="${y}"${layoutAttr}/>`
          );
        }
      }

      const relationParts: string[] = [];
      if (notebook.links) {
        for (const link of notebook.links) {
          if (hierarchicalPairs.has(`${link.fromId}→${link.toId}`)) continue;
          const smFrom = idMap.get(link.fromId);
          const smTo = idMap.get(link.toId);
          if (smFrom !== undefined && smTo !== undefined) {
            relationParts.push(`      <relation source="${smFrom}" target="${smTo}" asHierarchy="false"/>`);
          }
        }
      }

      const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<simplemind-mindmaps doc-version="3" generator="StudyBoard">
  <mindmap>
    <meta><title text="${escapeXml(notebook.title)}"/></meta>
    <topics>
${topicParts.join('\n')}
    </topics>
    <relations>
${relationParts.join('\n')}
    </relations>
  </mindmap>
</simplemind-mindmaps>`;

      docFolder?.file("mindmap.xml", xmlContent);

      const content = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 }
      });

      const url = URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${notebook.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.smmx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      console.log("Dịch vụ Backup: Xuất SimpleMind hoàn tất.");
    } catch (error) {
      console.error("Dịch vụ Backup: Lỗi khi xuất file .smmx", error);
      throw error;
    }
  }
};
